import axios, { AxiosError } from "axios";
import { useCallback, useContext } from "react";
import useSWR from "swr";
import { AuthContext } from "../../../context/AuthContext";
import { AdminEndpointEnum } from "../../../enums/endpoints";
import { ADMIN_API_URL } from "../../../utils/env";
import { ToastEnum, ToastStatusEnum, useToast } from "../../toast";

export type OndoHedgeStatus = "" | "pending" | "succeeded" | "failed";

export interface IOndoIntent {
  quote_id: string;
  account: string;
  provider_account: string;
  collateral: string;
  asset: string;
  is_buy: boolean;
  base_amount: string;
  quote_amount: string;
  price: string;
}

export interface IOndoSwap extends IOndoIntent {
  hedge_id: string;
  order_id: string;
  min_out?: string;
  version?: string;
  quote_status: string;
  hedge_status: string;
  retryable: boolean;
  attempts?: number;
  last_error?: string;
  tx_hash?: string;
  created_at?: string;
  expires_at?: string;
  reserved_at?: string;
  accepted_at?: string;
  hedged_at?: string;
  failed_at?: string;
  cancel_reason?: string;
}

export interface IOndoHedge extends IOndoIntent {
  hedge_id: string;
  order_id: string;
  status: Exclude<OndoHedgeStatus, "">;
  attempts: number;
  last_error?: string;
  tx_hash?: string;
  accepted_at?: string;
  hedged_at?: string;
  failed_at?: string;
  reattested_at?: string;
}

interface IOndoSwapsResponse {
  swaps: IOndoSwap[];
}

interface IOndoHedgesResponse {
  hedges: IOndoHedge[];
}

interface IAdminApiError {
  error?: string;
  message?: string;
}

export interface IOndoInventoryBalance {
  collateral: string;
  base_balance: string;
  usdc_balance: string;
}

export interface IOndoInventoryResponse {
  counterparty: string;
  inventory: IOndoInventoryBalance[];
  pending_hedge_count: number;
  failed_hedge_count: number;
  failed_hedges: IOndoHedge[];
  paused_assets: Record<string, string>;
}

interface IUseOndoOptions {
  status: OndoHedgeStatus;
  asset: string;
  limit: number;
}

const ondoAssetEndpoint = (asset: string, action: "pause" | "resume") =>
  ADMIN_API_URL(`ondo/assets/${encodeURIComponent(asset)}/${action}`);

const ondoHedgeEndpoint = (
  hedgeId: string,
  action?: "retry" | "mark-resolved"
) => {
  const suffix = action ? `/${action}` : "";
  return ADMIN_API_URL(
    `${AdminEndpointEnum.ONDO_HEDGES}/${encodeURIComponent(hedgeId)}${suffix}`
  );
};

const getErrorMessage = (error?: AxiosError) => {
  if (!error) {
    return "";
  }
  const payload = error.response?.data as IAdminApiError | undefined;
  return payload?.error || payload?.message || error.message;
};

export const useOndo = ({ status, asset, limit }: IUseOndoOptions) => {
  const { queryConfig, tokenId } = useContext(AuthContext);
  const { addToast } = useToast();

  const swapsEndpoint = ADMIN_API_URL(AdminEndpointEnum.ONDO_SWAPS);
  const hedgesEndpoint = ADMIN_API_URL(AdminEndpointEnum.ONDO_HEDGES);
  const inventoryEndpoint = ADMIN_API_URL(AdminEndpointEnum.ONDO_INVENTORY);

  const swapsSWR = useSWR<IOndoSwap[], AxiosError>(
    tokenId ? [swapsEndpoint, tokenId, status, asset, limit] : undefined,
    async () => {
      const response = await axios.get<IOndoSwapsResponse>(swapsEndpoint, {
        ...queryConfig(),
        params: {
          status: status || undefined,
          asset: asset.trim() || undefined,
          limit,
        },
      });
      return response.data.swaps ?? [];
    },
    { refreshInterval: 10000 }
  );

  const failedHedgesSWR = useSWR<IOndoHedge[], AxiosError>(
    tokenId ? [hedgesEndpoint, tokenId, "failed"] : undefined,
    async () => {
      const response = await axios.get<IOndoHedgesResponse>(hedgesEndpoint, {
        ...queryConfig(),
        params: {
          status: "failed",
        },
      });
      return response.data.hedges ?? [];
    },
    { refreshInterval: 10000 }
  );

  const inventorySWR = useSWR<IOndoInventoryResponse, AxiosError>(
    tokenId ? [inventoryEndpoint, tokenId] : undefined,
    async () => {
      const response = await axios.get<IOndoInventoryResponse>(
        inventoryEndpoint,
        {
          ...queryConfig(),
        }
      );
      return response.data;
    },
    { refreshInterval: 10000 }
  );

  const withAuthHeaders = useCallback(
    () => ({
      headers: {
        Authorization: `Bearer ${tokenId}`,
      },
    }),
    [tokenId]
  );

  const refresh = useCallback(async () => {
    await Promise.all([
      swapsSWR.mutate(),
      failedHedgesSWR.mutate(),
      inventorySWR.mutate(),
    ]);
  }, [failedHedgesSWR, inventorySWR, swapsSWR]);

  const handleSuccess = useCallback(
    (header: string, subheader?: string) => {
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header,
          subheader,
          status: ToastStatusEnum.SUCCESS,
        },
        4000
      );
    },
    [addToast]
  );

  const handleError = useCallback(
    (error: AxiosError, header: string) => {
      // eslint-disable-next-line no-console
      console.error(error);
      const payload = error.response?.data as any;
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header,
          subheader: payload?.error || payload?.message || "Unknown error",
          status: ToastStatusEnum.ERROR,
        },
        5000
      );
    },
    [addToast]
  );

  const retryHedge = useCallback(
    async (hedgeId: string) => {
      try {
        const response = await axios.post<{ tx_hash?: string }>(
          ondoHedgeEndpoint(hedgeId, "retry"),
          {},
          withAuthHeaders()
        );
        handleSuccess("Ondo hedge retried", response.data.tx_hash || hedgeId);
        await refresh();
      } catch (error) {
        handleError(error as AxiosError, "Ondo hedge retry failed");
      }
    },
    [handleError, handleSuccess, refresh, withAuthHeaders]
  );

  const markHedgeResolved = useCallback(
    async (hedgeId: string, txHash?: string) => {
      try {
        await axios.post(
          ondoHedgeEndpoint(hedgeId, "mark-resolved"),
          { tx_hash: txHash },
          withAuthHeaders()
        );
        handleSuccess("Ondo hedge marked resolved", txHash || hedgeId);
        await refresh();
      } catch (error) {
        handleError(error as AxiosError, "Mark Ondo hedge resolved failed");
      }
    },
    [handleError, handleSuccess, refresh, withAuthHeaders]
  );

  const pauseAsset = useCallback(
    async (targetAsset: string, reason: string) => {
      try {
        await axios.post(
          ondoAssetEndpoint(targetAsset, "pause"),
          { reason },
          withAuthHeaders()
        );
        handleSuccess("Ondo RFQ paused", targetAsset);
        await refresh();
      } catch (error) {
        handleError(error as AxiosError, "Pause Ondo RFQ failed");
      }
    },
    [handleError, handleSuccess, refresh, withAuthHeaders]
  );

  const resumeAsset = useCallback(
    async (targetAsset: string) => {
      try {
        await axios.post(
          ondoAssetEndpoint(targetAsset, "resume"),
          {},
          withAuthHeaders()
        );
        handleSuccess("Ondo RFQ resumed", targetAsset);
        await refresh();
      } catch (error) {
        handleError(error as AxiosError, "Resume Ondo RFQ failed");
      }
    },
    [handleError, handleSuccess, refresh, withAuthHeaders]
  );

  return {
    swaps: swapsSWR.error ? [] : swapsSWR.data ?? [],
    failedHedges: failedHedgesSWR.data ?? [],
    inventory: inventorySWR.data,
    isLoadingSwaps: swapsSWR.isLoading,
    isValidatingSwaps: swapsSWR.isValidating,
    isLoadingFailedHedges: failedHedgesSWR.isLoading,
    isValidatingFailedHedges: failedHedgesSWR.isValidating,
    isLoadingInventory: inventorySWR.isLoading,
    isValidatingInventory: inventorySWR.isValidating,
    swapsError: swapsSWR.error,
    swapsErrorMessage: getErrorMessage(swapsSWR.error),
    failedHedgesError: failedHedgesSWR.error,
    failedHedgesErrorMessage: getErrorMessage(failedHedgesSWR.error),
    inventoryError: inventorySWR.error,
    inventoryErrorMessage: getErrorMessage(inventorySWR.error),
    retryHedge,
    markHedgeResolved,
    pauseAsset,
    resumeAsset,
    refresh,
  };
};
